import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarClock, ExternalLink, Bell, Check } from "lucide-react";
import { Reveal } from "./Reveal";
import ShareButton from "./ShareButton";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import { getUtm } from "../lib/utm";

function useCountdown(target) {
  const [left, setLeft] = useState(() => Math.max(0, target - Date.now()));
  useEffect(() => {
    const id = setInterval(() => setLeft(Math.max(0, target - Date.now())), 1000);
    return () => clearInterval(id);
  }, [target]);
  const s = Math.floor(left / 1000);
  return {
    done: left <= 0,
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    mins: Math.floor((s % 3600) / 60),
    secs: s % 60,
  };
}

function Unit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <span className="font-display text-3xl font-bold tabular-nums text-white sm:text-4xl">
        {String(value).padStart(2, "0")}
      </span>
      <span className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-white/40">{label}</span>
    </div>
  );
}

function NotifyForm({ release }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done

  const submit = async (e) => {
    e.preventDefault();
    if (!email || status === "loading") return;
    setStatus("loading");
    const utm = getUtm();
    try {
      await api.post("/fans", {
        email,
        signupSource: "upcoming_notify",
        releaseId: release.internalId,
        utmSource: utm.utm_source || null,
        utmCampaign: utm.utm_campaign || null,
      });
      track("email_signup", { releaseId: release.internalId });
      setStatus("done");
      setEmail("");
    } catch {
      setStatus("idle");
    }
  };

  if (status === "done") {
    return (
      <div data-testid={`notify-success-${release.slug}`} className="mt-4 flex items-center gap-2 rounded-xl border border-gold/30 bg-gold/10 px-4 py-3 text-sm text-gold">
        <Check size={16} /> You're on the list — we'll ping you when <span className="font-semibold">{release.title}</span> drops.
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`notify-form-${release.slug}`} className="mt-4">
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-white/50">
        <Bell size={12} className="text-gold" /> Get notified when it drops
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          data-testid={`notify-email-${release.slug}`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className="h-12 flex-1 rounded-full border border-white/15 bg-ink/60 px-5 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-gold"
        />
        <button
          data-testid={`notify-submit-${release.slug}`}
          type="submit"
          disabled={status === "loading"}
          className="inline-flex h-12 items-center justify-center gap-1.5 rounded-full bg-gold px-6 text-xs font-bold uppercase tracking-wide text-black transition-transform active:scale-95 hover:bg-gold-2 disabled:opacity-60"
        >
          <Bell size={14} /> {status === "loading" ? "…" : "Notify Me"}
        </button>
      </div>
    </form>
  );
}

function UpcomingCard({ release, index }) {
  const target = new Date(`${release.releaseDate}T00:00:00`).getTime();
  const c = useCountdown(target);
  const dateLabel = new Date(target).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  return (
    <motion.div
      data-testid={`upcoming-${release.slug}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      className="relative overflow-hidden rounded-3xl border border-gold/20 bg-surface/60"
    >
      <div className="grid gap-0 sm:grid-cols-[minmax(0,200px)_1fr]">
        <div className="relative aspect-square sm:aspect-auto">
          <img src={release.artwork} alt={`${release.title} artwork`} loading="lazy" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent sm:bg-gradient-to-r" />
        </div>
        <div className="p-6 sm:p-7">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <CalendarClock size={13} /> Coming Soon
          </div>
          <h3 className="font-display text-2xl font-bold uppercase leading-none tracking-tight text-white sm:text-3xl">
            {release.title}
          </h3>
          <p className="mt-2 text-sm text-gold">DJ Código · {release.releaseType}</p>
          <p className="mt-0.5 text-xs text-white/40">Drops {dateLabel}</p>

          {c.done ? (
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-gold px-4 py-2 text-xs font-bold uppercase tracking-wide text-black">Out Now</span>
              {release.spotifyURL && (
                <a
                  href={release.spotifyURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("spotify_cta_clicked", { releaseId: release.internalId })}
                  className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-4 py-2 text-xs font-bold uppercase tracking-wide text-gold"
                >
                  Listen <ExternalLink size={12} />
                </a>
              )}
            </div>
          ) : (
            <div className="mt-5 flex items-center gap-4 sm:gap-6">
              <Unit value={c.days} label="Days" />
              <span className="font-display text-2xl text-white/20">:</span>
              <Unit value={c.hours} label="Hrs" />
              <span className="font-display text-2xl text-white/20">:</span>
              <Unit value={c.mins} label="Min" />
              <span className="font-display text-2xl text-white/20">:</span>
              <Unit value={c.secs} label="Sec" />
            </div>
          )}

          {!c.done && <NotifyForm release={release} />}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            {release.spotifyURL && !c.done && (
              <a
                href={release.spotifyURL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/10 px-4 py-2.5 text-xs font-bold uppercase tracking-wide text-gold"
              >
                Pre-Save <ExternalLink size={12} />
              </a>
            )}
            <ShareButton release={release} />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function UpcomingReleases({ releases }) {
  if (!releases || releases.length === 0) return null;
  return (
    <section id="upcoming" data-testid="upcoming-section" className="px-6 py-16 sm:px-10 sm:py-24 lg:px-16">
      <div className="mx-auto max-w-5xl">
        <Reveal>
          <p className="mb-2 text-xs font-bold uppercase tracking-[0.3em] text-gold">Próximamente</p>
          <h2 className="mb-10 font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-5xl">
            Upcoming Releases
          </h2>
        </Reveal>
        <div className="space-y-5">
          {releases.map((r, i) => (
            <UpcomingCard key={r.internalId} release={r} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
